import Modal, { Props as ModalProps } from '@hero/ui-kit/components/Modal';
import Button from '@hero/ui-kit/inputs/Button';
import Form from '@hero/ui-kit/inputs/Form';
import Section from '@hero/ui-kit/layout/Section';
import P from '@hero/ui-kit/typography/P';
import React from 'react';

import * as yup from 'yup';
import useProspectAction from '../../../api/useProspectAction';
import InlineLoader from '../../../../../components/InlineLoader';
import QueryInlineError from '../../../../../components/QueryInlineError';
import phoneValidator from '@hero/validators/libphonenumber';
import Input from '@hero/ui-kit/inputs/Input';

const updatePhoneActionsSchema = yup
    .object()
    .shape({
        phone: yup.lazy((val) =>
            !val ? yup.string().trim().required('Phone number is required') : phoneValidator
        ) as unknown as yup.StringSchema<string, object>
    })
    .defined();

export type UpdatePhoneActionsParams = yup.InferType<typeof updatePhoneActionsSchema>;

type UpdatePhoneModalProps = {
    onCancel?: () => void;
    prospectId: string;
} & Pick<ModalProps, 'externalControls'>;

const UpdatePhoneModal: React.FC<UpdatePhoneModalProps> = ({
    externalControls,
    onCancel,
    prospectId
}) => {
    const [, setExternalState] = externalControls ?? [];

    const {
        mutateAsync: prospectAction,
        isPending: isActionPending,
        error,
        isSuccess,
        reset
    } = useProspectAction();

    const handleCancel = () => {
        onCancel?.();
        setExternalState && setExternalState(false);
        reset();
    };

    const handleSubmit = (values: UpdatePhoneActionsParams) => {
        prospectAction({
            prospect_id: prospectId,
            action: 'set_phone',
            phone: values.phone
        });
    };

    return (
        <Modal
            isCancelable
            onClose={handleCancel}
            externalControls={externalControls}
            type={isSuccess ? 'success' : 'neutral'}
            styles={{ width: '50rem', minWidth: '50rem' }}
        >
            <P centered strong size="large">
                Update Phone Number
            </P>

            <Section noDefaultPadding centered>
                {isSuccess ? (
                    <>
                        <P size="large" centered>
                            Phone updated successfully.
                        </P>
                        <Button type="button" variant="outlineSecondary" onClick={handleCancel}>
                            Close
                        </Button>
                    </>
                ) : (
                    <>
                        {isActionPending ? (
                            <InlineLoader />
                        ) : (
                            <Form
                                validationSchema={updatePhoneActionsSchema}
                                submitFn={handleSubmit}
                            >
                                <Input name="phone" displayName="New Phone Number" />
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        columnGap: '1rem',
                                        marginTop: '1.6rem'
                                    }}
                                >
                                    <Button
                                        type="button"
                                        variant="outlineSecondary"
                                        onClick={handleCancel}
                                        noDefaultMargin
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        disabled={isActionPending}
                                        noDefaultMargin
                                    >
                                        Submit
                                    </Button>
                                </div>

                                {error ? <QueryInlineError centered error={error} /> : null}
                            </Form>
                        )}
                    </>
                )}
            </Section>
        </Modal>
    );
};

export default UpdatePhoneModal;
